import React, { memo, useCallback, useState } from 'react';
import {
  Box,
  makeStyles,
  Menu,
  styled,
  PopoverOrigin,
  withStyles
} from '@material-ui/core';

import SMenuItem, { ISMenuItem } from './MenuItem';
import SMoreButton from '../smore-button';
import eventEmitter from '../../libs/EventEmitter';

const StyledMenu = withStyles(({ spacing }) => ({
  paper: {
    marginTop: spacing(2),
    padding: `0px ${spacing(0)}px`,
    borderRadius: 8,
    left: spacing(0),

    '& ul': {
      paddingTop: 0,
      paddingBottom: 0,

      '& div > li > div': {
        maxHeight: 60,
        height: '100%'
      }
    }
  }
}))(Menu);

export const StyledMenuV2 = styled(Menu)(({ theme }) => ({
  margin: theme.spacing('2px', 1),

  '& .MuiPaper-root': {
    borderRadius: 20,
    minWidth: 178,
    boxShadow:
      '0px 24px 48px -8px rgba(48, 54, 60, 0.12), 0px 4px 4px 0px rgba(48, 54, 60, 0.04)',

    '& ul': {
      padding: 12,

      '& div > li > div': {
        borderRadius: 24,
        maxHeight: 48,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        padding: theme.spacing('12px', '12px')
      }
    }
  }
}));

const useIconButtonStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  button: {
    borderRadius: 15,
    width: 16
  },
  buttonActive: {
    backgroundColor: '#E0ECFF'
  }
}));

const defaultSubmenuAnchorOrigin: PopoverOrigin = {
  horizontal: 'left',
  vertical: 'top'
};

export interface ISDropdownMenu {
  menuItems: ISMenuItem[];
  children?: JSX.Element;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  submenuTransformOrigin?: PopoverOrigin;
  submenuAnchorOrigin?: PopoverOrigin;
  onClose?: () => void;
  customMenuClass?: any;
  hasSubmenu?: boolean;
  direction?: 'horizontal' | 'vertical';
  // ! This will be removed once the new design is implemented
  newDesign?: boolean;
}

const SDropdownMenu = ({
  newDesign = false,
  menuItems,
  onClose,
  children,
  hasSubmenu,
  anchorOrigin,
  transformOrigin,
  customMenuClass = {},
  submenuAnchorOrigin = defaultSubmenuAnchorOrigin,
  submenuTransformOrigin,
  direction = 'vertical'
}: ISDropdownMenu) => {
  const styles = useIconButtonStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      eventEmitter.emit('sdropdown-menu-opened');
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  const SMenu = newDesign ? StyledMenuV2 : StyledMenu;
  const subMenuStyle = { marginLeft: -12, height: 30 };

  return (
    <Box
      className={styles.buttonContainer}
      onClick={e => e.stopPropagation()}
      style={hasSubmenu && newDesign ? subMenuStyle : {}}>
      <Box
        width="100%"
        onClick={handleOpenMenu}
        onMouseEnter={hasSubmenu && handleOpenMenu}>
        {children || (
          <SMoreButton
            active={Boolean(anchorEl)}
            width={14}
            direction={direction}
          />
        )}
      </Box>
      {Boolean(anchorEl) && (
        <SMenu
          anchorEl={anchorEl}
          keepMounted
          open
          onClose={hasSubmenu ? () => setAnchorEl(null) : handleClose}
          elevation={3}
          getContentAnchorEl={null}
          anchorOrigin={
            anchorOrigin || {
              vertical: 'bottom',
              horizontal: 'right'
            }
          }
          transformOrigin={
            transformOrigin || {
              vertical: 'top',
              horizontal: 'right'
            }
          }
          className={customMenuClass}>
          <div onMouseLeave={hasSubmenu && (() => setAnchorEl(null))}>
            {menuItems.map(item => (
              <SMenuItem
                key={item.text}
                icon={item.icon}
                text={item.text}
                divider={item.divider}
                subText={item.subText}
                onClick={item.onClick}
                closeOnClick={item.closeOnClick}
                boldText={item.boldText}
                borderedBox={item.borderedBox}
                color={item.color}
                disabled={item.disabled}
                tooltipText={item.tooltipText}
                handleClose={handleClose}
                submenu={item.submenu}
                newDesign={newDesign}
                tooltipPlacement={item.tooltipPlacement}
                submenuAnchorOrigin={submenuAnchorOrigin}
                submenuTransformOrigin={submenuTransformOrigin}
              />
            ))}
          </div>
        </SMenu>
      )}
    </Box>
  );
};

export default memo(SDropdownMenu);
